/** JoinRequests.js:
 * Communication with Firestore to manage opt-in requests
 * and create associated notifications.
 */

// ===============================
// Imports
// ===============================
import { db } from "../firebase";
import {
    collection,
    addDoc,
    serverTimestamp,
    doc,
    updateDoc,
    getDoc,
    query, where,
    getDocs
} from "firebase/firestore";

// ===============================
// Sends a join request to a trip and creates a notification for the trip owner.
// ===============================
export const sendJoinRequest = async ({ fromUserId, toUserId, tripId }) => {
    try {
        // Add a new join request to the "joinRequests" collection
        await addDoc(collection(db, "joinRequests"), {
            fromUserId, // ID of the user sending the request
            toUserId, // ID of the trip owner (receiver)
            tripId, // ID of the trip the user wants to join
            status: "pending", // Initial status is "pending"
            createdAt: serverTimestamp(),
        });

        // Retrieving the sending user's information
        const userRef = doc(db, "users", fromUserId);
        const userSnap = await getDoc(userRef);

        let senderImage = "";
        let senderName = "משתמש";

        if (userSnap.exists()) {
            const userData = userSnap.data();
            senderImage = userData.image || "";
            senderName = userData.title || userData.name || "משתמש";
        }

        // Create a notification in the "notifications" collection
        await addDoc(collection(db, "notifications"), {
            userId: toUserId,
            type: "join_request",
            tripId,
            fromUserId,
            senderName,
            senderImage,
            isRead: false,
            createdAt: serverTimestamp(),
        });

        console.log("Join request and notification sent!");
    } catch (error) {
        console.error("Error sending join request or notification:", error);
    }
};

export const sendGroupJoinRequest = async ({ fromUserId, toUserId, tripId }) => {
    try {
        await addDoc(collection(db, "joinRequests"), {
            fromUserId,
            toUserId,
            tripId,
            status: "pending",
            createdAt: serverTimestamp(),
        });

        const userRef = doc(db, "users", fromUserId);
        const userSnap = await getDoc(userRef);

        let senderImage = "";
        let senderName = "משתמש";

        if (userSnap.exists()) {
            const userData = userSnap.data();
            senderImage = userData.image || "";
            senderName = userData.title || userData.name || "משתמש";
        }

        await addDoc(collection(db, "notifications"), {
            userId: toUserId,
            type: "group_join_request",
            tripId,
            fromUserId,
            senderName,
            senderImage,
            isRead: false,
            createdAt: serverTimestamp(),
        });

        console.log("Group join request and notification sent!");
    } catch (error) {
        console.error("Error sending group join request or notification:", error);
    }
};


/**
 * Updates the status of a join request (e.g., accepted or declined)
 * @param {string} requestId - The document ID of the join request
 * @param {string} response - The new status: "accepted" or "declined"
 */
export const respondToJoinRequest = async (requestId, response) => {
    try {
        // Update the "status" field in the join request document
        await updateDoc(doc(db, "joinRequests", requestId), {
            status: response, // Set to either "accepted" or "declined"
        });

        console.log("Join request status updated!");
    } catch (error) {
        console.error("Error updating join request status:", error);
    }
};

/**
 * Finds the join request ID for a given trip and user.
 * @param {string} tripId - ID of the trip.
 * @param {string} fromUserId - ID of the user who sent the join request.
 * @returns {Promise<string|null>}
 */
export const findJoinRequestId = async (tripId, fromUserId) => {
    try {
        const q = query(
            collection(db, "joinRequests"),
            where("tripId", "==", tripId),
            where("fromUserId", "==", fromUserId)
        );

        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            return snapshot.docs[0].id;
        } else {
            return null;
        }
    } catch (error) {
        console.error("Error finding join request ID:", error);
        return null;
    }
};

/**
 * Deletes all join requests for a given trip except the approved one.
 * @param {string} tripId - ID of the trip.
 * @param {string} approvedUserId - ID of the user whose request was approved.
 */
export const deleteOtherJoinRequestsForSingleTrip = async (tripId, approvedUserId) => {
    try {
        const q = query(
            collection(db, "joinRequests"),
            where("tripId", "==", tripId)
        );
        const snapshot = await getDocs(q);
        const batchDeletes = [];
        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            if (data.fromUserId !== approvedUserId) {
                batchDeletes.push(docSnap.ref.delete());
            }
        });
        await Promise.all(batchDeletes);
        console.log("Deleted other join requests for trip:", tripId);
    } catch (error) {
        console.error("Error deleting other join requests:", error);
    }
};
